import { type ButtonProps } from '@atlaskit/button/new';
import { type FlagProps } from '@atlaskit/flag';
import {
  type ModalDialogProps,
  type ModalTitleProps,
  type ModalHeaderProps,
  type ModalBodyProps,
  type ModalFooterProps,
} from '@atlaskit/modal-dialog';
import { type token, type ThemeState } from '@atlaskit/tokens';
import { type PartialDeep } from 'type-fest';

import {
  type BoardId,
  type ContentId,
  type DocumentTransformerCallback,
  type DocumentTransformerFormat,
} from '@canvas/model/board';
import { type ANONYMOUS_ID } from '@canvas/model/identity';
import { type SpotlightScreenType, type SpotlightScreen } from '@canvas/model/onboarding';
import {
  type EntitlementFeatureBody,
  type PaywallFeatureStateFromEntitlement,
} from '@canvas/paywall-types';

export interface FrameContainer {
  // Container (space) key https://support.atlassian.com/confluence-cloud/docs/choose-a-space-key/
  key: string;
  name: string;
  source: string;
}

export enum UserTypes {
  // Logged in, part of Confluence
  CONFLUENCE_USER = 'confluence-user',
  // Logged in, part of the org
  ORG_MEMBER = 'org-member',
  // Logged in, not part of the org
  ATLASSIAN_USER = 'atlassian-user',
  // Not logged in
  ANONYMOUS = 'anonymous',
}

// The raw values we get can get from the content API
export interface RawWhiteboardSessionData {
  accountId: string | typeof ANONYMOUS_ID | null;
  tenantId: string | null | undefined;
  publicLinkUserType: UserTypes;
}

export interface WhiteboardSessionData {
  accountId: string;
  tenantId: string;
  publicLinkUserType: UserTypes;
}

export interface WhiteboardQueryParams {
  openTemplatePicker?: boolean;
}

export interface Collaborator {
  name: string;
  color: string;
  onClick?: () => void;
  followCursor?: boolean;
  avatar: {
    src?: string;
  };
}

export interface Collaborators {
  collaborators: Collaborator[];
  me: Collaborator;
}
export interface MainAction {
  id: string;
  text?: string;
  icon?: string;
  tooltipText?: string;
  onClick?: () => void;
  expanded?: boolean;
  appearance?: string;
  isSelected?: boolean;
  styles?: {
    [prop: string]: string;
  };
}

export interface MainActions {
  mainActions: MainAction[];
}

export interface Item {
  id: string;
  text: string;
  shortcutText?: string;
  onClick?: () => void;
  url?: string;
  target?: string;
}

export interface ItemGroup {
  id: string;
  items: Item[];
  text?: string;
}

export interface MoreActions {
  items: (Item | ItemGroup)[][];
}

export interface BoardStatus {
  status: string;
  tooltipText?: string;
}

export type EditionUpgradeCTAProps = {
  title: string;
  body: string;
  feature: string;
  image: {
    src: {
      default: string;
      dark: string;
    };
    alt: string;
  };
  learnMoreLink: {
    href: string;
    message: string;
  };
};

export type ResolveWhiteboardCommentIds = {
  contentId: string | undefined;
  referenceId: string | undefined;
};

export type ResolvedCommentHandler = (resolvedIds: ResolveWhiteboardCommentIds) => void;
export type CopyAllToClipboardHandler = () => void;

export type WhiteboardInternalModalProps = {
  modalHeader: {
    title: {
      content: string;
      props?: Omit<ModalTitleProps, 'children'>;
    };
    props?: Omit<ModalHeaderProps, 'children'>;
  };
  modalBody: {
    content: string;
    props?: Omit<ModalBodyProps, 'children'>;
  };
  modalFooter: {
    buttons: {
      label: string;
      shouldCloseModal?: boolean;
      props?: Omit<ButtonProps, 'children'>;
    }[];
    props?: Omit<ModalFooterProps, 'children'>;
  };
  props?: Omit<ModalDialogProps, 'children'>;
  image?: string;
};

export interface WhiteboardInternalModalConfiguration {
  type: 'dialog' | 'dialogList';
}

export interface WhiteboardDialogConfiguration
  extends WhiteboardInternalModalConfiguration,
    WhiteboardInternalModalProps {
  type: 'dialog';
}

export interface WhiteboardOnboardingDialogListConfiguration
  extends WhiteboardInternalModalConfiguration {
  type: 'dialogList';
  dialogList: WhiteboardInternalModalProps[];
  props?: Omit<ModalDialogProps, 'children'>;
  bodyTextHeight?: string;
}

export type OnboardingStartHandler = (initialSpotlightScreen?: string) => void;

/**
 * Event that can be triggered in Confluence Frontend
 * Note: This should ideally be replaced with a type library from Confluence Frontend
 */
export type ConfluenceEvent = 'copy' | 'hideSidePanel' | string;
export type ConfluenceEventHandler = () => void;

// 'Flag' refers to Atlaskit's <Flag>, NOT feature flags!

// if adding more icons, ensure updating the type in AFM as well
export type ContentTypesFlagIconString = 'feedback' | 'info';

// NOTE: This is directly lifted from Confluence's 'flags' package and should be kept in sync
type FlagType = 'success-circle' | 'success' | 'info' | 'error' | 'warning' | 'custom';

// We omit the 'icon' since we currently cannot pass JSX through the bridge API, plus if we specify a
// type other than custom then this gets handled by confluence automagically. We also specify
// the 'id' and 'key' manually in AFM since Whiteboards should not have to worry about these.
// For docs on atlaskit flag: https://atlassian.design/components/flag/examples

// Aside: This should really be sourced from @confluence/flag's FlagDescriptor type, but this is
// connie-specific, so we instead reference the shared @atlaskit/flag type and translate this to
// what we need in Connie at the CFE level
// Annoyingly, due to type mismatches between @atlaskit/flag and @confluence/flags, the latter doesn't
// specify onDismissed, opting instead for onClose, hence we must omit it too and replace it with onClose
type BaseFlagProps = Omit<FlagProps, 'icon' | 'key' | 'onDismissed'> & {
  onClose?: VoidFunction;
};

interface StandardFlagProps extends BaseFlagProps {
  type: Exclude<FlagType, 'custom'>;
}
interface CustomFlagProps extends BaseFlagProps {
  type: 'custom';
  iconString: ContentTypesFlagIconString;
  iconColor?: ReturnType<typeof token>;
}
export type ContentTypesFlagProps = StandardFlagProps | CustomFlagProps;

export type DocumentTransformer<T> = {
  format: DocumentTransformerFormat;
  transform: DocumentTransformerCallback<T>;
};

export type FeedbackCollectorFeatureName =
  | 'Template Picker'
  | 'AI Generate Stickies'
  | 'Miro Importer'
  | 'Mural Importer'
  | 'AI Clustering';

// Any new feature level feedback should go through here :)
type AdditionalFeedbackCollectorFields = {
  'Whiteboards Feature': FeedbackCollectorFeatureName;
};

export type GlobalFeedbackCollectorComponentProps = {
  onClose: () => void;
  onCloseComplete: () => void;
  feedbackTitle?: string;
  summaryPlaceholder?: string;
  // Additional metadata to be attached to the feedback context
  // NOTE: made mandatory to ensure we always have feature-level metadata attached to WBs feedback
  additionalFields: AdditionalFeedbackCollectorFields;

  // Excluded fields from CFE type (kept for reference)
  // ❌ feedbackSource: FeedbackSource; - Hardcoded to WHITEBOARD in CFE
  // ❌ feedbackTitleDetails?: React.ReactChild; - JSX which we ignore
  // ❌ showTypeField?: boolean; - Overwritten to true in CFE
  // ❌ contentType?: string; - Hardcoded to WHITEBOARD in CFE
};

export type PublicLink = {
  // Is this whiteboard a public link
  isEnabled: boolean;
  // Is it anonymously editable
  isEditEnabled: boolean;
  // Method to call to refresh the values of the public link
  refetch: () => void;
};

export interface BaseContentAPI<TokenGeneric> {
  sessionData: RawWhiteboardSessionData;

  goToContent: (id: ContentId | BoardId, query?: WhiteboardQueryParams) => void;
  goToContainer: (id: string) => void;
  goToHome: VoidFunction;

  onTokenInit: (handler: (context: TokenGeneric | undefined) => void) => void;
  onTokenChange: (handler: (context: TokenGeneric | undefined) => void) => void;
  preloadToken: (id?: BoardId) => Promise<void>;
  preloadContent?: (id?: ContentId) => Promise<void>;
  getToken: (id?: BoardId) => Promise<TokenGeneric | undefined>;
  refreshToken?: (id?: BoardId) => Promise<void>;

  onContainerInit: (handler: (context: FrameContainer) => void) => void;
  onContainerChange: (handler: (context: FrameContainer) => void) => void;

  onRouteChange?: (handler: (route: string) => void) => void;

  onContentLoadSucceeded?: (attributes: Record<string, any>) => void;
  onContentLoadFailed?: (error: Error, attributes: Record<string, any>) => void;
  onContentLoadAborted?: (reason: string, attributes: Record<string, any>) => void;

  isFeatureActive?: (name: string) => boolean;
  platformFeatureFlagResolver?: (name: string) => boolean;

  updateCollaborators?: (collaborators: Collaborators) => void;
  updateMainActions?: (items: MainActions) => void;
  updateMoreActions?: (items: MoreActions) => void;
  updateBoardStatus?: (status: BoardStatus) => void;

  /**
   * Used to register a callback in Confluence that sets the active
   * value in Canvas's onboarding state to true to indicate that Canvas can
   * start its onboarding flow. This is needed because the onboarding eligibility
   * and timing logic lives in Confluence.
   *
   * There are also now multiple onboarding entrypoints (Beta, GA "Creator" and GA "Viewer")
   * so it needs to accept an optional initial screen
   *
   * @param callback A callback that's meant to accept an initial onboarding screen and
   * trigger onboarding. Our implementation sets `state.active` to true and `state.screen`
   * to `initialSpotlightScreen` in the onboarding state
   */
  registerStartOnboardingCallback?: (callback: OnboardingStartHandler) => void;

  /**
   * Called whenever the onboarding spotlight screen changes, to allow Confluence to
   * take action (e.g., fire an analytics event, display confetti, etc)
   * @param screen The spotlight screen name that was just rendered
   */
  onOnboardingChange?: (screen: SpotlightScreen) => void;

  /**
   * This is intended to be called once the whiteboards onboarding flow has
   * finished in Canvas. It calls a function that's been registered in Confluence
   * to handle any cleanup needed to end the overall onboarding tour.
   *
   * @param screen A string representing the name/identifier of the onboarding screen
   * that the flow finished on
   */
  onOnboardingEnd?: (screen: string) => void;

  onOrgIdChange?: (handler: (id: string) => void) => void;

  getOrgId?: () => string | undefined;

  /**
   * Asks CFE to render paywall CTA with provided options
   * CFE will return a promise that is resolved when the CTA closes
   */
  showEditionUpgradeCTA?: (props: EditionUpgradeCTAProps) => Promise<void>;
  closeEditionUpgradeCTA?: () => void;

  /**
   * Asks CFE to open/close the global feedback collector
   */
  showFeedbackCollectorModal?: (props: GlobalFeedbackCollectorComponentProps) => void;
  closeFeedbackCollectorModal?: () => void;

  /***
   * Used to keep the theme state in sync between Canvas and Confluence
   */
  onThemeChange?: (handler: (theme: Partial<ThemeState>) => void) => void;
  getCurrentTheme?: () => Partial<ThemeState> | undefined;

  /***
   * Used to tell confluence that our app is ready to be shown
   */
  onThemeLoaded?: VoidFunction;
  /**
   * Generic method to trigger one of many tasks
   */
  triggerIntegration: (args: {
    id: string;
    onSuccess: (args: unknown) => void;
    onFailure?: (args: unknown) => void;
    otherProps: any;
  }) => Promise<void>;
  /**
   * Returns if a given task is available and ready to use
   */
  checkIntegration: (args: { id: string }) => boolean;

  onOnboardingReady?: (eligibleInitialScreens: SpotlightScreenType[]) => void;

  /**
   * Asks CFE to open/close a modal
   */
  showWhiteboardInternalModal?: (
    config: WhiteboardInternalModalConfiguration
  ) => Promise<void>;
  closeWhiteboardInternalModal?: () => void;

  /**
   * Politely asks CFE to open/close the <Flag /> component with provided props. NOTE: refers to
   * https://atlassian.design/components/flag/examples, NOT "feature flags" as a concept
   */
  showFlag?: (props: ContentTypesFlagProps) => void;

  /**
   * Closes the Flag
   */
  hideFlag?: (flagId: string) => void;

  /**
   * Called in canvas to register the reopenResolvedCommentCallback in the resolved comments service
   */
  registerOnReopenResolvedComment?: (handler: ResolvedCommentHandler) => void;

  /**
   * Called in canvas to register the events in Confluence Frontend
   */
  registerEventHandler?: (
    event: ConfluenceEvent,
    handler: ConfluenceEventHandler
  ) => void;

  /**
   * Trigger events in Confluence Frontend
   */
  triggerEvent?: (event: ConfluenceEvent) => void;

  /**
   * Called in canvas to unregister an event in Confluence Frontend
   */
  unregisterEventHandler?: (event: ConfluenceEvent) => void;

  /**
   * **IMPORTANT:** The content API is accessible to public (anonymous) users.
   * Consider whether the transformer being registered should be restricted to
   * users who have at least read access to the content.
   *
   * @description
   * Called in canvas when board data has been loaded and ECS has been booted
   * up. Used to register a callback that enables a caller in Confluence to
   * download a transformed document (e.g. in WDF) on demand.
   *
   * May be called once for each supported transformer, e.g. we may look into
   * supporting image previews or other formats for extensibility in the future.
   *
   * @example
   * Usage in Confluence:
   * ```ts
   * // whiteboard-bridge-api-builder.ts
   * build(): WhiteboardBridgeAPI {
   *   return {
   *     // ...
   *     registerDocumentTransformerCallback: (transformer) => {
   *       // assign `transformer` to a method in the service to be called later
   *       this.services.transformerService.registerDocumentTransformerCallback(transformer);
   *     }
   *     // ...
   *   }
   * }
   *
   * // file to invoke transformer in
   * const transformer = this.services.transformerService.get('WDF');
   * const { transformedDoc: wDoc } = transformer.transform();
   * ```
   */
  registerDocumentTransformerCallback?: (
    transformer: DocumentTransformer<unknown>
  ) => void;

  /**
   * This should be called when fetching the current loaded version name (will return null on latest version)
   * @returns {string | null} The name of the current version, as it should be shown in CFE
   */
  getVersionName?: () => string;

  /**
   * Is this request coming from an external Confluence view
   * aka /wiki/external/<external-board-id>
   */
  isExternalShareRequest?: boolean;

  /**
   * Fetches metadata about the state of the externally shared public link
   * @returns {PublicLink} The public link state
   */
  getPublicLink?: () => PublicLink;
  /**
   * Refreshes the metadata of the public link
   */
  refetchPublicLink?: () => void;

  /**
   * Fetches the creator id of the current content
   */
  getContentCreatorId?: () => string | null | undefined;
}

/**
 * This is a refactor of the original `BaseContentAPI` type to be more abstracted.
 * It is currently not in use
 */
export interface BaseContentAPIv2<TokenGeneric> {
  sessionData: RawWhiteboardSessionData;

  /**
   * Methods related to triggering functionality within Whiteboards
   * This is intended for use cases where parent applications need to trigger or retrieve data from Whiteboards
   */
  whiteboardsAPI: {
    theme: {
      /***
       * Used to keep the theme state in sync between Canvas and Confluence
       */
      onThemeChange?: (handler: (theme: Partial<ThemeState>) => void) => void;
      getCurrentTheme?: () => Partial<ThemeState> | undefined;

      /***
       * Used to tell confluence that our app is ready to be shown
       */
      onThemeLoaded?: VoidFunction;
    };

    wdf: {
      /**
       * **IMPORTANT:** The content API is accessible to public (anonymous) users.
       * Consider whether the transformer being registered should be restricted to
       * users who have at least read access to the content.
       *
       * @description
       * Called in canvas when board data has been loaded and ECS has been booted
       * up. Used to register a callback that enables a caller in Confluence to
       * download a transformed document (e.g. in WDF) on demand.
       *
       * May be called once for each supported transformer, e.g. we may look into
       * supporting image previews or other formats for extensibility in the future.
       *
       * @example
       * Usage in Confluence:
       * ```ts
       * // whiteboard-bridge-api-builder.ts
       * build(): WhiteboardBridgeAPI {
       *   return {
       *     // ...
       *     registerDocumentTransformerCallback: (transformer) => {
       *       // assign `transformer` to a method in the service to be called later
       *       this.services.transformerService.registerDocumentTransformerCallback(transformer);
       *     }
       *     // ...
       *   }
       * }
       *
       * // file to invoke transformer in
       * const transformer = this.services.transformerService.get('WDF');
       * const { transformedDoc: wDoc } = transformer.transform();
       * ```
       */
      registerDocumentTransformerCallback?: (
        transformer: DocumentTransformer<unknown>
      ) => void;
    };

    versionHistory: {
      /**
       * This should be called when fetching the current loaded version name (will return null on latest version)
       * @returns {string | null} The name of the current version, as it should be shown in CFE
       */
      getVersionName?: () => string;
    };
  };

  /**
   * Methods related to triggering functionality within Confluence Frontend
   * This is intended for use cases where Whiteboards need to trigger or retrieve data from Confluence Frontend
   */
  confluenceAPI: {
    /**
     * Authentication methods
     */
    authentication: {
      onTokenInit: (handler: (context: TokenGeneric | undefined) => void) => void;
      onTokenChange: (handler: (context: TokenGeneric | undefined) => void) => void;
      preloadToken: (id?: BoardId) => Promise<void>;
      preloadContent?: (id?: ContentId) => Promise<void>;
      getToken: (id?: BoardId) => Promise<TokenGeneric | undefined>;
      refreshToken?: (id?: BoardId) => Promise<void>;
    };

    /**
     * Navigation methods
     */
    navigation: {
      goToContent: (id: ContentId | BoardId, query?: WhiteboardQueryParams) => void;
      goToContainer: (id: string) => void;
      goToHome: VoidFunction;
      onContainerInit: (handler: (context: FrameContainer) => void) => void;
      onContainerChange: (handler: (context: FrameContainer) => void) => void;
      onRouteChange?: (handler: (route: string) => void) => void;
      onContentLoadSucceeded?: (attributes: Record<string, any>) => void;
      onContentLoadFailed?: (error: Error, attributes: Record<string, any>) => void;
      onContentLoadAborted?: (reason: string, attributes: Record<string, any>) => void;
    };

    /**
     * Feature flag methods
     */
    featureFlags: {
      isFeatureActive?: (name: string) => boolean;
      platformFeatureFlagResolver?: (name: string) => boolean;
    };

    /**
     * Methods related to modifying the canvas shell
     */
    shell: {
      updateCollaborators?: (collaborators: Collaborators) => void;
      updateMainActions?: (items: MainActions) => void;
      updateMoreActions?: (items: MoreActions) => void;
      updateBoardStatus?: (status: BoardStatus) => void;
    };

    /**
     * Methods related to onboarding
     */
    onboarding: {
      /**
       * Used to register a callback in Confluence that sets the active
       * value in Canvas's onboarding state to true to indicate that Canvas can
       * start its onboarding flow. This is needed because the onboarding eligibility
       * and timing logic lives in Confluence.
       *
       * There are also now multiple onboarding entrypoints (Beta, GA "Creator" and GA "Viewer")
       * so it needs to accept an optional initial screen
       *
       * @param callback A callback that's meant to accept an initial onboarding screen and
       * trigger onboarding. Our implementation sets `state.active` to true and `state.screen`
       * to `initialSpotlightScreen` in the onboarding state
       */
      registerStartOnboardingCallback?: (callback: OnboardingStartHandler) => void;

      /**
       * Called whenever the onboarding spotlight screen changes, to allow Confluence to
       * take action (e.g., fire an analytics event, display confetti, etc)
       * @param screen The spotlight screen name that was just rendered
       */
      onOnboardingChange?: (screen: SpotlightScreen) => void;

      /**
       * This is intended to be called once the whiteboards onboarding flow has
       * finished in Canvas. It calls a function that's been registered in Confluence
       * to handle any cleanup needed to end the overall onboarding tour.
       *
       * @param screen A string representing the name/identifier of the onboarding screen
       * that the flow finished on
       */
      onOnboardingEnd?: (screen: string) => void;
      onOnboardingReady?: (eligibleInitialScreens: SpotlightScreenType[]) => void;
    };

    /**
     * TODO: confirm whether these are needed(?)
     */
    onOrgIdChange?: (handler: (id: string) => void) => void;

    analytics: {
      /**
       * Used for analytics client
       */
      getOrgId?: () => string | undefined;
    };

    /** Methods related to showing modals and flags */
    modals: {
      /**
       * Asks CFE to render paywall CTA with provided options
       * CFE will return a promise that is resolved when the CTA closes
       */
      showEditionUpgradeCTA?: (props: EditionUpgradeCTAProps) => Promise<void>;
      closeEditionUpgradeCTA?: () => void;

      /**
       * Asks CFE to open/close the global feedback collector
       */
      showFeedbackCollectorModal?: (props: GlobalFeedbackCollectorComponentProps) => void;
      closeFeedbackCollectorModal?: () => void;

      /**
       * Asks CFE to open/close a Modal
       */
      showWhiteboardInternalModal?: (
        config: WhiteboardInternalModalConfiguration
      ) => Promise<void>;
      closeWhiteboardInternalModal?: () => void;

      /**
       * Politely asks CFE to open/close the <Flag /> component with provided props. NOTE: refers to
       * https://atlassian.design/components/flag/examples, NOT "feature flags" as a concept
       */
      showFlag?: (props: ContentTypesFlagProps) => void;

      /**
       * Closes the Flag
       */
      hideFlag?: (flagId: string) => void;
    };

    comments: {
      /**
       * Called in canvas to register the reopenResolvedCommentCallback in the resolved comments service
       */
      registerOnReopenResolvedComment?: (handler: ResolvedCommentHandler) => void;
    };

    /** TODO: Consider converting to a bespoke function? */
    loom: {
      /**
       * Generic method to trigger one of many tasks
       */
      triggerIntegration: (args: {
        id: string;
        onSuccess: (args: unknown) => void;
        onFailure?: (args: unknown) => void;
        otherProps: any;
      }) => Promise<void>;
      /**
       * Returns if a given task is available and ready to use
       */
      checkIntegration: (args: { id: string }) => boolean;
    };

    /** TODO: Consider converting to a bespoke function? */
    /**
     * Event based API
     */
    events: {
      confluenceAPI: {
        /**
         * Called in canvas to register the events in Confluence Frontend
         */
        registerConfluenceEventHandler?: (
          event: ConfluenceEvent,
          handler: ConfluenceEventHandler
        ) => void;

        /**
         * Trigger events in Confluence Frontend
         */
        triggerConfluenceEvent?: (event: ConfluenceEvent) => void;

        /**
         * Called in canvas to unregister an event in Confluence Frontend
         */
        unregisterConfluenceEventHandler?: (event: ConfluenceEvent) => void;
      };
    };

    /**
     * Contains methods related to public links
     */
    publicLinks: {
      /**
       * Fetches metadata about the state of the externally shared public link
       * @returns {PublicLink} The public link state
       */
      getPublicLink?: () => PublicLink;
      /**
       * Refreshes the metadata of the public link
       */
      refetchPublicLink?: () => void;
    };

    contentData: {
      /**
       * Fetches the creator id of the current content
       */
      getContentCreatorId?: () => string | null | undefined;
    };

    /**
     * Is this request coming from an external Confluence view
     * aka /wiki/external/<external-board-id>
     */
    isExternalShareRequest?: boolean;
  };
}

/**
 * The content API, passed through from Confluence, as-is, without any form of
 * transformation or wrapping added to it
 *
 * Avoid using the Raw content API as much as possible as it means that data
 * coming from Confluence Frontend will need to be converted before being used
 * by the rest of the app - Either that or raw-types from Confluence Frontend
 * end up being leaked into Canvas.
 */
export type RawContentAPI = BaseContentAPI<RawFrameToken>;

/**
 * A version of {@link RawContentAPI} that has been wrapped to output versions
 * of the bridge API that are more meaningful to our app. Use
 * this type whenever possible instead of {@link RawContentAPI}.
 */
export type ContentAPI = BaseContentAPI<FrameToken>;

export type ARIString = string;
export type JWTString = string;
export type Edition = 'free' | 'standard' | 'premium';

/**
 * Expected data security policy type shape.
 */
export type DataSecurityPolicy = {
  [DataSecurityPolicyAction.pageExport]: boolean;
};
/**
 * Expected policy type shape.
 */
export type Policies = {
  dataSecurityPolicy: DataSecurityPolicy;
};
/**
 * DataSecurityPolicy with all keys optional.
 */
export type DataSecurityPolicyBody = PartialDeep<DataSecurityPolicy>;
/**
 * Policies with all keys optional.
 */
export type PoliciesBody = PartialDeep<{
  dataSecurityPolicy: DataSecurityPolicyBody;
}>;

export enum DataSecurityPolicyAction {
  pageExport = 'pageExport',
}

export interface RawFrameToken {
  container: ARIString;
  board: ARIString;
  accessToken: JWTString;
  mediaToken: JWTString;
  mediaBaseUrl: string;
  mediaCollectionId: string;
  /**
   * undefined is technically not a valid value. We leave this as undefined to
   * protect against cases where Confluence Frontend changes the key (since we
   * don't have contract tests).
   */
  features?: EntitlementFeatureBody;
  /**
   * edition is only to be used for the purpose of analytics and, hence, only
   * appears as edition in the RawFrameToken. In the transformed FrameToken,
   * which is the version that should be used throughout the app, it is named
   * editionOnlyUseForAnalytics.
   */
  edition?: Edition;

  /**
   * Similar to the features field, we have to leave this undefined since there
   * aren't contract tests with the Confluence Frontend
   */
  policies?: PoliciesBody;
}

/**
 * Represents a version of {@link RawFrameToken} that has been transformed into
 * a more Canvas-developer friendly version of the original frame token payload
 * @privateRemark Similar to how we convert ARI strings to ARI objects as soon
 * as we receive a payload from a network request, we try convert the token to
 * something more meaningful to avoid unnecessary duplication of transformations
 * anywhere else in our code
 */
export type FrameToken = Omit<RawFrameToken, 'features' | 'edition' | 'policies'> & {
  features?: PaywallFeatureStateFromEntitlement;
  dataSecurityPolicy?: DataSecurityPolicy;
  editionOnlyUseForAnalytics?: Edition;
};

/**
 * State that we store in the frame valtio state
 * @privateRemark We store paywall state outside the frame token state which is
 * why we omit 'features'
 */
export type FrameState = Omit<FrameToken, 'features'>;

export interface FrameContext {
  data: FrameState;
}

export type ContentAPIFactory = (rawContentAPI: unknown) => ContentAPI | undefined;

export interface ContentWindowApi {
  title: string;
  href: string;
  isTopLevel: boolean;
  contentAPI: ContentAPI | undefined;
  reload: () => void;
  dispatchKeyboardEvent: (event: KeyboardEvent) => void;
}

export type RawTokenHandler = (context: RawFrameToken | undefined) => void;
export type ContainerHandler = (context: FrameContainer) => void;
export type RouteHandler = (route: string) => void;
